/**
 * Static config for the 10 standard Business Trackers.
 *
 * Each entry drives:
 *   - Dashboard card rendering (icon, label, accountsLabel template)
 *   - Add Account modal field set (fields[])
 *   - Accounts table columns (cols[])
 *   - Frequency-driven payment grid (monthly | quarterly | annual)
 *
 * Field labels and select options align with the BeeNexa Trackers v4
 * mockup so the modal shows the exact same input set Elizabeth saw in
 * the reference. The Auto-pay Day text input is shown unconditionally;
 * if Payment Method = Manual it's just ignored.
 */

// Reused at the end of every tracker's fields[] so users always see
// the auto-pay knobs after the tracker-specific inputs.
const PAYMENT_FIELDS = [
    { name: 'paymentMethod', label: 'Payment Method', type: 'select', required: true,
      options: ['Manual', 'Auto-pay'] },
    { name: 'autoPayDay', label: 'Auto-pay Day (1-31)', type: 'text',
      placeholder: 'e.g. 15 (only for Auto-pay)' },
];

export const TRACKERS = {
    electricity: {
        key: 'electricity',
        icon: '⚡',
        label: 'Electricity',
        frequency: 'monthly',
        cols: ['Location', 'Address', 'Account Name', 'Provider', 'Account #', 'Rate/kWh', 'Contract End', 'Payment', 'Status'],
        fields: [
            { name: 'label', label: 'Location Name', type: 'text', required: true,
              placeholder: 'e.g. Fuego 777' },
            { name: 'address', label: 'Address', type: 'text', required: true,
              placeholder: 'e.g. 1234 Mason Rd, Katy, TX 77450' },
            { name: 'accountName', label: 'Account Name', type: 'text', required: true,
              placeholder: 'e.g. Elizabeth Rodriguez' },
            { name: 'provider', label: 'Provider', type: 'select', required: true,
              options: ['Reliant Energy', 'TXU Energy', 'Just Energy', 'Direct Energy', 'Gexa Energy', 'CenterPoint', 'Champion', 'Other'] },
            { name: 'accountNumber', label: 'Account Number', type: 'text', required: true,
              placeholder: 'e.g. REL-449021' },
            { name: 'ratePerKwh', label: 'Rate / kWh', type: 'text', placeholder: 'e.g. $0.092' },
            { name: 'contractEnd', label: 'Contract End Date', type: 'date', required: true },
            ...PAYMENT_FIELDS,
        ],
    },
    dumpster: {
        key: 'dumpster',
        icon: '🗑',
        label: 'Dumpster',
        frequency: 'monthly',
        cols: ['Location', 'Address', 'Account Name', 'Vendor', 'Account #', 'Plan (Yards)', 'Monthly Cost', 'Contract End', 'Payment', 'Status'],
        fields: [
            { name: 'label', label: 'Location Name', type: 'text', required: true },
            { name: 'address', label: 'Address', type: 'text', required: true },
            { name: 'accountName', label: 'Account Name', type: 'text', required: true },
            { name: 'vendor', label: 'Vendor', type: 'select', required: true,
              options: ['Waste Management', 'Republic Services', 'Local Hauler', 'Other'] },
            { name: 'accountNumber', label: 'Account Number', type: 'text', required: true,
              placeholder: 'e.g. WM-882013' },
            { name: 'planSize', label: 'Plan Size', type: 'select', required: true,
              options: ['2 Yards', '3 Yards', '4 Yards', '6 Yards', '8 Yards', '20-yd roll-off'] },
            { name: 'pickupFrequency', label: 'Pickup Frequency', type: 'select',
              options: ['1x/week', '2x/week', '3x/week', 'On-demand'] },
            { name: 'monthlyCost', label: 'Monthly Cost', type: 'text', required: true,
              placeholder: 'e.g. $185.00' },
            { name: 'contractEnd', label: 'Contract End Date', type: 'date', required: true },
            ...PAYMENT_FIELDS,
        ],
    },
    insurance: {
        key: 'insurance',
        icon: '🛡',
        label: 'Insurance',
        frequency: 'monthly',
        cols: ['Location', 'Address', 'Insurance Co.', 'Policy #', 'Type', 'Term', 'Premium', 'Expires', 'Payment', 'Status'],
        fields: [
            { name: 'label', label: 'Location / Insured', type: 'text', required: true },
            { name: 'address', label: 'Address', type: 'text', required: true },
            { name: 'insuranceCompany', label: 'Insurance Company', type: 'text', required: true,
              placeholder: 'e.g. The Hartford' },
            { name: 'policyNumber', label: 'Policy Number', type: 'text', required: true,
              placeholder: 'e.g. GL-882013' },
            { name: 'policyType', label: 'Policy Type', type: 'select', required: true,
              options: ['General Liability', 'Property', 'Workers Compensation', 'Commercial Auto', 'Auto', 'Health', 'Life', 'Umbrella', 'Other'] },
            { name: 'termLength', label: 'Term Length', type: 'select', required: true,
              options: ['6 Months', '1 Year'] },
            { name: 'paymentType', label: 'Payment Type', type: 'select', required: true,
              options: ['Monthly', 'Paid in Full'] },
            { name: 'totalPremium', label: 'Total Premium', type: 'text', required: true,
              placeholder: 'e.g. $2,400' },
            { name: 'coverageAmount', label: 'Coverage Amount', type: 'text',
              placeholder: 'e.g. $1,000,000' },
            { name: 'deductible', label: 'Deductible', type: 'text' },
            { name: 'startDate', label: 'Start Date', type: 'date', required: true },
            { name: 'expirationDate', label: 'Expiration Date', type: 'date', required: true },
            ...PAYMENT_FIELDS,
        ],
    },
    alarm: {
        key: 'alarm',
        icon: '🚨',
        label: 'Alarm Company',
        frequency: 'monthly',
        cols: ['Location', 'Address', 'Account Name', 'Alarm Co.', 'Account #', 'Plan', 'Monthly Cost', 'Contract End', 'Payment', 'Status'],
        fields: [
            { name: 'label', label: 'Location Name', type: 'text', required: true },
            { name: 'address', label: 'Address', type: 'text', required: true },
            { name: 'accountName', label: 'Account Name', type: 'text', required: true },
            { name: 'alarmCompany', label: 'Alarm Company', type: 'select', required: true,
              options: ['ADT', 'Brinks', 'Vivint', 'SimpliSafe', 'Local Co.', 'Other'] },
            { name: 'accountNumber', label: 'Account Number', type: 'text', required: true,
              placeholder: 'e.g. ADT-882013' },
            { name: 'planService', label: 'Plan / Service', type: 'text', required: true,
              placeholder: 'e.g. 24/7 Monitoring, Cameras, Fire' },
            { name: 'monthlyCost', label: 'Monthly Cost', type: 'text', required: true,
              placeholder: 'e.g. $79.99' },
            { name: 'contractEnd', label: 'Contract End Date', type: 'date', required: true },
            ...PAYMENT_FIELDS,
        ],
    },
    pest: {
        key: 'pest',
        icon: '🐛',
        label: 'Pest Control',
        frequency: 'monthly',
        cols: ['Location', 'Address', 'Account Name', 'Provider', 'Account #', 'Frequency', 'Cost / Service', 'Contract End', 'Payment', 'Status'],
        fields: [
            { name: 'label', label: 'Location Name', type: 'text', required: true },
            { name: 'address', label: 'Address', type: 'text', required: true },
            { name: 'accountName', label: 'Account Name', type: 'text', required: true },
            { name: 'provider', label: 'Provider', type: 'select', required: true,
              options: ['Terminix', 'Orkin', 'ABC Home & Commercial', 'Aptive', 'Local Co.', 'Other'] },
            { name: 'accountNumber', label: 'Account Number', type: 'text', required: true,
              placeholder: 'e.g. TRM-882013' },
            { name: 'serviceFrequency', label: 'Frequency', type: 'select', required: true,
              options: ['Monthly', 'Quarterly'] },
            { name: 'pestType', label: 'Pest Type Covered', type: 'text',
              placeholder: 'Roaches, ants, rodents, termites' },
            { name: 'costPerService', label: 'Cost per Service', type: 'text', required: true,
              placeholder: 'e.g. $85.00' },
            { name: 'contractEnd', label: 'Contract End Date', type: 'date', required: true },
            ...PAYMENT_FIELDS,
        ],
    },
    mortgage: {
        key: 'mortgage',
        icon: '🏦',
        label: 'Mortgage / Rent',
        frequency: 'monthly',
        cols: ['Property', 'Address', 'Type', 'Company / Landlord', 'Account #', 'Monthly Pmt', 'Balance / Lease End', 'Payment', 'Status'],
        fields: [
            { name: 'label', label: 'Property Name', type: 'text', required: true },
            { name: 'address', label: 'Address', type: 'text', required: true },
            { name: 'mortgageType', label: 'Type', type: 'select', required: true,
              options: ['Mortgage', 'Rent'] },
            { name: 'lender', label: 'Mortgage Company / Landlord', type: 'text', required: true,
              placeholder: 'e.g. Chase Bank or Katy Plaza LLC' },
            { name: 'accountNumber', label: 'Account / Lease Number', type: 'text', required: true,
              placeholder: 'e.g. CHA-7782019' },
            { name: 'monthlyPayment', label: 'Monthly Payment', type: 'text', required: true,
              placeholder: 'e.g. $3,420.00' },
            { name: 'originalLoanAmount', label: 'Original Loan Amount (Mortgage only)', type: 'text',
              placeholder: 'e.g. $385,000' },
            { name: 'interestRate', label: 'Interest Rate / APR % (Mortgage only)', type: 'text',
              placeholder: 'e.g. 6.75' },
            { name: 'loanTerm', label: 'Loan Term — Years (Mortgage only)', type: 'text',
              placeholder: 'e.g. 30' },
            { name: 'loanStartDate', label: 'Loan Start (Mortgage) / Lease Start (Rent)', type: 'date', required: true },
            { name: 'leaseEndDate', label: 'Lease End Date (Rent only)', type: 'date' },
            ...PAYMENT_FIELDS,
        ],
    },
    tenantRent: {
        key: 'tenantRent',
        icon: '💰',
        label: 'Tenant Rent',
        frequency: 'monthly',
        incoming: true,
        lockedBelongs: 'property',
        cols: ['Property', 'Tenant', 'Lease Period', 'Monthly Rent', 'Deposit', 'Late Fee Policy', 'Balance Owed', 'Status'],
        fields: [
            { name: 'label', label: 'Property Name', type: 'text', required: true,
              placeholder: 'e.g. Rental — Pearland' },
            { name: 'address', label: 'Property Address', type: 'text', required: true,
              placeholder: 'e.g. 1730 Magnolia St, Pearland, TX' },
            { name: 'tenantName', label: 'Tenant Name', type: 'text', required: true,
              placeholder: 'e.g. Carlos Mendez' },
            { name: 'tenantPhone', label: 'Tenant Phone', type: 'text',
              placeholder: 'e.g. (713) 555-1234' },
            { name: 'tenantEmail', label: 'Tenant Email', type: 'text',
              placeholder: 'e.g. carlos@example.com' },
            { name: 'monthlyRent', label: 'Monthly Rent', type: 'text', required: true,
              placeholder: 'e.g. $1,850' },
            { name: 'securityDeposit', label: 'Security Deposit', type: 'text',
              placeholder: 'e.g. $1,850' },
            { name: 'lateFeeAmount', label: 'Late Fee Amount', type: 'text',
              placeholder: 'e.g. $50' },
            { name: 'lateFeeAfterDay', label: 'Late Fee After Day', type: 'number',
              placeholder: 'e.g. 5' },
            { name: 'leaseStart', label: 'Lease Start Date', type: 'date', required: true },
            { name: 'leaseEnd', label: 'Lease End Date', type: 'date', required: true },
        ],
    },
    salesTax: {
        key: 'salesTax',
        icon: '💼',
        label: 'Sales Tax',
        frequency: 'monthly',
        dualField: true,
        cols: ['Business', 'Address', 'Frequency', 'Tax ID', 'Filing Method', 'Last Filed', 'Next Due', 'Payment', 'Status'],
        fields: [
            { name: 'label', label: 'Business Entity', type: 'text', required: true,
              placeholder: 'Doing-business-as name' },
            { name: 'address', label: 'Address', type: 'text', required: true },
            { name: 'filingFrequency', label: 'Filing Frequency', type: 'select', required: true,
              options: ['Monthly', 'Quarterly'] },
            { name: 'permitNumber', label: 'Tax ID / Permit Number', type: 'text', required: true,
              placeholder: 'e.g. 32-088123456' },
            { name: 'filingMethod', label: 'Filing Method', type: 'select', required: true,
              options: ['TX Comptroller Webfile', 'CPA Filing', 'Tax Software (TurboTax, etc.)', 'EDI', 'Paper', 'Other'] },
            { name: 'cpaName', label: 'CPA Name (if applicable)', type: 'text',
              placeholder: 'e.g. Smith & Associates CPA' },
            { name: 'defaultTaxRate', label: 'Default Tax Rate (ref only)', type: 'text',
              placeholder: 'e.g. 8.25%' },
            { name: 'lastFiledDate', label: 'Last Filed Date', type: 'date' },
            { name: 'nextDueDate', label: 'Next Due Date', type: 'date', required: true },
            ...PAYMENT_FIELDS,
        ],
    },
    propertyTax: {
        key: 'propertyTax',
        icon: '🏡',
        label: 'Property Tax',
        frequency: 'annual',
        subTaxLabels: ['County', 'School ISD', 'MUD', 'City', 'Other'],
        cols: ['Property', 'Address', 'County', 'School ISD', 'Appraised Value', 'Homestead', 'Total Tax (Annual)', 'Payment Method', 'Status'],
        fields: [
            { name: 'label', label: 'Property Name', type: 'text', required: true },
            { name: 'address', label: 'Property Address', type: 'text', required: true },
            { name: 'county', label: 'County', type: 'select', required: true,
              options: ['Harris', 'Fort Bend', 'Brazoria', 'Galveston', 'Montgomery', 'Other'] },
            { name: 'schoolDistrict', label: 'School District / ISD', type: 'text', required: true,
              placeholder: 'e.g. Katy ISD, Pearland ISD' },
            { name: 'appraisedValue', label: 'Appraised Value (current year)', type: 'text', required: true,
              placeholder: 'e.g. $285,000' },
            { name: 'homestead', label: 'Homestead Exemption?', type: 'select',
              options: ['No', 'Yes — Primary residence'] },
            { name: 'hasMud', label: 'Has MUD?', type: 'select',
              options: ['No', 'Yes'] },
            { name: 'mudName', label: 'MUD Number / Name', type: 'text',
              placeholder: 'e.g. Harris County MUD 501' },
            { name: 'cityTax', label: 'City Tax (if incorporated)', type: 'select',
              options: ['No (unincorporated)', 'Yes'] },
            { name: 'otherSpecialDistrict', label: 'Other Special District', type: 'text',
              placeholder: 'e.g. Hospital District, College, ESD' },
            { name: 'accountNumber', label: 'Tax Account # / Geo ID', type: 'text',
              placeholder: 'e.g. 0123-45-678-90-9' },
            { name: 'propertyType', label: 'Property Type', type: 'select', required: true,
              options: ['Residential — Primary', 'Residential — Rental', 'Commercial', 'Vacant Land'] },
            { name: 'paymentMethod', label: 'Payment Method', type: 'select', required: true,
              options: ['Manual', 'Auto-pay (escrow)'] },
        ],
    },
    annualTax: {
        key: 'annualTax',
        icon: '📅',
        label: 'Annual Tax',
        frequency: 'annual',
        subTaxLabels: ['Franchise Tax (TX) · May 15', 'Federal Income Tax · Apr 15', '—', '—', '—'],
        cols: ['Business / Entity', 'Parent / Holding', 'Ownership %', 'Entity Type', 'Tax ID (EIN)', 'Annualized Revenue', 'Franchise Tax Status', 'Federal Form', 'Status'],
        fields: [
            { name: 'label', label: 'Entity Name', type: 'text', required: true },
            { name: 'entityType', label: 'Entity Type', type: 'select', required: true,
              options: ['LLC (Single-member)', 'LLC (Multi-member)', 'C-Corporation', 'S-Corporation', 'Partnership', 'Sole Proprietorship', 'Holding Company'] },
            { name: 'parentCompany', label: 'Parent / Holding Company', type: 'select',
              options: ['— Top-level (no parent) —', 'Rodriguez Holdings LLC', 'Rodriguez Real Estate Holdings LLC', 'External (not owned)', 'Other'] },
            { name: 'ownership', label: 'Ownership %', type: 'select', required: true,
              options: ['100%', '75-99%', '50-74%', '25-49%', '1-24%', '0% (operator only)'] },
            { name: 'managementCompany', label: 'Management Company', type: 'text',
              placeholder: 'e.g. V Data Systems, BeeNexa Management' },
            { name: 'ein', label: 'Tax ID / EIN', type: 'text', required: true,
              placeholder: 'e.g. 88-1234567' },
            { name: 'annualizedRevenue', label: 'Annualized Total Revenue', type: 'select', required: true,
              options: ['Below $2.65M (No Tax Due)', 'Above $2.65M (Tax Owed)', 'N/A (not our filing)'] },
            { name: 'franchiseFiling', label: 'Franchise Tax Filing Type', type: 'select',
              options: ['PIR Only (No Tax Due)', 'EZ Computation', 'Long Form', 'OIR Only', 'Not filed by us (operator only)'] },
            { name: 'federalForm', label: 'Federal Tax Form', type: 'select', required: true,
              options: ['1040 Schedule C (Sole/SMLLC)', '1040 Schedule E (Rental)', '1065 Partnership', '1120 C-Corp', '1120-S S-Corp', 'Operator only — not filed by us', 'Other'] },
            { name: 'filingMethod', label: 'Filing Method', type: 'select', required: true,
              options: ['CPA Filing', 'TX Comptroller Webfile', 'TurboTax / Tax Software', 'Self-filed', 'Other'] },
            { name: 'cpaName', label: 'CPA Name (if applicable)', type: 'text',
              placeholder: 'e.g. Smith & Associates CPA' },
            { name: 'taxYearReported', label: 'Tax Year Reported', type: 'text', required: true,
              placeholder: 'e.g. 2025 (filed in 2026)' },
        ],
    },
    // -------------------------------------------------------------------
    // Tracker 11 — Licenses & Permits.
    // Recurring renewals only (NOT one-time/onboarding permits). Tab is
    // renamed to "Renewals" and uses a custom timeline view (no payRows
    // grid). Per spec § 16. The Add modal uses a multi-permit builder
    // (template + checkbox list) rather than a single-record form.
    // -------------------------------------------------------------------
    licenses: {
        key: 'licenses',
        icon: '📜',
        label: 'Licenses & Permits',
        frequency: 'asNeeded',
        customView: 'licenses',
        paymentsTabLabel: 'Renewals',
        addButtonLabel: '+ Add Renewal',
        cols: ['Location', 'Type', 'Issuing Authority', 'Permit #', 'Issued', 'Expires', 'Renewal Cost', 'Frequency', 'Status'],
        // The Add Renewal modal is a multi-permit builder (template + checkbox
        // list), not a flat field form. fields[] is left empty so the standard
        // FieldRow renderer doesn't try to draw anything for it.
        fields: [],
    },
    // -------------------------------------------------------------------
    // Tracker 12 — Maintenance Orders.
    // Custom Orders view; the Accounts tab is hidden (the order list IS
    // the primary view). Status flow: Requested → Assigned → In Progress
    // → Completed. Per spec § 17.
    // -------------------------------------------------------------------
    maintenance: {
        key: 'maintenance',
        icon: '🔧',
        label: 'Maintenance Orders',
        frequency: 'asNeeded',
        customView: 'maintenance',
        hideAccountsTab: true,
        paymentsTabLabel: 'Orders',
        addButtonLabel: '+ New Order',
        addModalTitle: 'Create New Order',
        cols: ['Title', 'Location', 'Category', 'Priority', 'Vendor', 'Quoted', 'Actual', 'Date Requested', 'Status'],
        // Simplified create form per spec § 17. Vendor / cost / quotes are
        // added later via the order detail popup.
        fields: [
            { name: 'label', label: 'Order Title', type: 'text', required: true,
              placeholder: 'e.g. Fuga de agua en cocina' },
            { name: 'description', label: 'Description / Details', type: 'text', required: true,
              placeholder: 'Describe the issue in detail...' },
            { name: 'orderLocation', label: 'Location', type: 'text', required: true,
              placeholder: 'e.g. El Campesino' },
            { name: 'category', label: 'Category', type: 'select', required: true,
              options: ['Plumbing', 'Electrical', 'HVAC', 'Refrigeration', 'Painting', 'Roofing', 'Flooring', 'Appliance Repair', 'Pest / Cleaning', 'Landscaping', 'Security / Alarm', 'General', 'Other'] },
            { name: 'priority', label: 'Priority', type: 'select', required: true,
              options: ['Urgent (24-48hr)', 'Normal (1-2 weeks)', 'Low (when possible)'] },
            { name: 'vendor', label: 'Vendor / Contractor', type: 'text',
              placeholder: 'e.g. CoolTech HVAC, or "TBD"' },
            { name: 'vendorPhone', label: 'Vendor Phone / Contact', type: 'text',
              placeholder: 'e.g. (713) 555-0100' },
            { name: 'dateScheduled', label: 'Date Scheduled (if known)', type: 'date' },
        ],
    },
};

// Recurring permit templates per business type. Used by the Licenses
// multi-permit builder to pre-check the typical permits.
export const LICENSE_PERMIT_TYPES = [
    { id: 'business_license', icon: '📜', label: 'Business License', authority: 'City', cost: '$150', freq: 'Annual' },
    { id: 'health', icon: '🍽️', label: 'Health Permit', authority: 'County Health Dept', cost: '$300', freq: 'Annual' },
    { id: 'tabc_bg', icon: '🥃', label: 'TABC License (BG - Beer/Wine)', authority: 'TX Alcoholic Bev Comm', cost: '$1,000', freq: 'Annual' },
    { id: 'tabc_mb', icon: '🍸', label: 'TABC License (MB - Mixed Beverage)', authority: 'TX Alcoholic Bev Comm', cost: '$3,000', freq: 'Annual' },
    { id: 'fire', icon: '🚒', label: 'Fire Inspection', authority: 'City Fire Marshal', cost: '$75', freq: 'Annual' },
    { id: 'alarm', icon: '🚨', label: 'Alarm Permit', authority: 'City Police / PD', cost: '$50', freq: 'Annual' },
    { id: 'elevator', icon: '🛗', label: 'Elevator Inspection', authority: 'TX Dept of Licensing & Reg', cost: '$200', freq: 'Annual' },
    { id: 'food_mgr', icon: '👨‍🍳', label: 'Food Manager Certification', authority: 'TX Dept of State Health Svcs', cost: '$50', freq: '5-year' },
    { id: 'music', icon: '🎵', label: 'Music License (BMI/ASCAP)', authority: 'BMI / ASCAP', cost: '$500', freq: 'Annual' },
    { id: 'health_inspection', icon: '🔍', label: 'Health Inspection (recurring)', authority: 'County Health Dept', cost: '$0', freq: 'Annual' },
];

export const LICENSE_TEMPLATES = {
    restaurant: { label: '🍽️ Restaurant / Bar', permits: ['business_license', 'health', 'tabc_bg', 'fire', 'alarm', 'food_mgr', 'music', 'health_inspection'] },
    bar: { label: '🥃 Bar Only', permits: ['business_license', 'tabc_mb', 'fire', 'alarm', 'music'] },
    gameroom: { label: '🎰 Game Room (8-liner)', permits: ['business_license', 'fire', 'alarm'] },
    retail: { label: '🏬 Retail Store', permits: ['business_license', 'fire', 'alarm'] },
    office: { label: '🏢 Office / Service', permits: ['business_license', 'fire'] },
    custom: { label: '🛠️ Custom (start from scratch)', permits: [] },
};

export const MAINTENANCE_CATEGORY_ICONS = {
    Plumbing: '🚿',
    Electrical: '⚡',
    HVAC: '❄️',
    Refrigeration: '🧊',
    Painting: '🎨',
    Roofing: '🏠',
    Flooring: '🟫',
    'Appliance Repair': '📦',
    'Pest / Cleaning': '🐛',
    Landscaping: '🌳',
    'Security / Alarm': '🚨',
    General: '🔧',
    Other: '🔧',
};

export const TRACKER_KEYS = [
    'electricity', 'dumpster', 'insurance', 'alarm', 'pest',
    'mortgage', 'tenantRent', 'salesTax', 'propertyTax', 'annualTax',
    'licenses', 'maintenance',
];

export const MONTH_LABELS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
